import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { FORM_TYPES } from "@/lib/formTypes";
import ImageField from "@/components/ImageField";
import AutoTextarea from "@/components/AutoTextarea";
import { Save } from "lucide-react";
import { toast } from "sonner";

// Neutraler dunkler Hintergrund für editierbare Felder.
const EDIT_FIELD = "bg-zinc-800/60 border-zinc-700/60";

const EMPTY = {
  name: "", form_type: "spielform", focus_area_ids: [],
  dauer_min: null, spieler_von: null, spieler_bis: null,
  organisation: "", material: "", ablauf: "",
  variationen: "", coaching: "", tipps: "", fotos: [],
};

// Wiederverwendbarer Editor für eine Übungs-/Spielform.
// Legt beim Speichern selbst an (POST) oder aktualisiert (PUT, wenn `editId`).
export default function FormEditorDialog({
  open, editId, initial, focusAreas = [], onClose, onSaved, title,
}) {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF({ ...EMPTY, ...(initial || {}) });
  }, [open, initial]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleFocus = (id) =>
    setF((p) => ({
      ...p,
      focus_area_ids: p.focus_area_ids.includes(id)
        ? p.focus_area_ids.filter((x) => x !== id)
        : [...p.focus_area_ids, id],
    }));

  const save = async () => {
    if (!f.name.trim()) { toast.error("Bitte einen Namen vergeben"); return; }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(),
        form_type: f.form_type,
        focus_area_ids: f.focus_area_ids || [],
        dauer_min: f.dauer_min === "" ? null : f.dauer_min,
        spieler_von: f.spieler_von === "" ? null : f.spieler_von,
        spieler_bis: f.spieler_bis === "" ? null : f.spieler_bis,
        organisation: f.organisation || "",
        material: f.material || "",
        ablauf: f.ablauf || "",
        variationen: f.variationen || "",
        coaching: f.coaching || "",
        tipps: f.tipps || "",
        fotos: (f.fotos || []).slice(0, 1),
      };
      const { data } = editId
        ? await api.put(`/exercise-forms/${editId}`, payload)
        : await api.post("/exercise-forms", payload);
      toast.success(editId ? "Form aktualisiert" : "Form gespeichert");
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 w-[calc(100vw-1.5rem)] max-w-lg lg:max-w-2xl max-h-[90dvh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="p-5 pb-3 shrink-0 border-b border-zinc-800">
          <DialogTitle className="font-display text-lg font-bold tracking-tight" data-testid="form-editor-title">
            {title || (editId ? "Form bearbeiten" : "Neue Form")}
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Übungs- oder Spielform für deine Bibliothek
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto p-5 space-y-4">
          <div>
            <Label className="text-[10px] sm:text-[11px] uppercase tracking-widest text-emerald-400/80 font-semibold">Bezeichnung</Label>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)}
              placeholder="z. B. 4 gegen 4 + 2 Außenspieler"
              className={`${EDIT_FIELD} text-zinc-100 placeholder:text-zinc-500 mt-1 h-11 focus-visible:ring-2 focus-visible:ring-emerald-500/50 focus-visible:border-emerald-500`}
              data-testid="form-editor-name" />
          </div>

          <div>
            <Label className="text-[10px] sm:text-[11px] uppercase tracking-widest text-emerald-400/80 font-semibold">Form-Typ</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5" data-testid="form-editor-types">
              {FORM_TYPES.map((t) => {
                const active = f.form_type === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => set("form_type", t.key)}
                    className={
                      "min-h-[40px] rounded-md border text-xs font-semibold tracking-wide transition " +
                      (active
                        ? "bg-emerald-600 border-emerald-500 text-white shadow-sm"
                        : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-emerald-500/40 hover:text-zinc-100")
                    }
                    data-testid={`form-editor-type-${t.key}`}>
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-[10px] sm:text-[11px] uppercase tracking-widest text-emerald-400/80 font-semibold">Trainingsschwerpunkte</Label>
            <div className="flex flex-wrap gap-2 mt-1.5" data-testid="form-editor-focus">
              {focusAreas.map((fa) => {
                const active = f.focus_area_ids.includes(fa.id);
                return (
                  <button key={fa.id} type="button" onClick={() => toggleFocus(fa.id)}
                    className={
                      "min-h-[34px] px-3 rounded-full border text-xs font-medium transition " +
                      (active
                        ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300"
                        : "bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-emerald-500/40 hover:text-zinc-100")
                    }
                    data-testid={`form-editor-focus-${fa.id}`}>
                    {fa.name}
                  </button>
                );
              })}
              {focusAreas.length === 0 && <div className="text-xs text-zinc-500">Noch keine Schwerpunkte.</div>}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <NumField label="Dauer (min)" value={f.dauer_min} onChange={(v) => set("dauer_min", v)} testId="form-editor-dauer" />
            <NumField label="Sp. min" value={f.spieler_von} onChange={(v) => set("spieler_von", v)} testId="form-editor-svon" />
            <NumField label="Sp. max" value={f.spieler_bis} onChange={(v) => set("spieler_bis", v)} testId="form-editor-sbis" />
          </div>

          <TA label="Ablauf / Beschreibung" rows={4} value={f.ablauf} onChange={(v) => set("ablauf", v)} testId="form-editor-ablauf" />
          <TA label="Organisation / Aufbau" value={f.organisation} onChange={(v) => set("organisation", v)} testId="form-editor-org" />
          <TA label="Coachingpunkte" rows={3} value={f.coaching} onChange={(v) => set("coaching", v)} testId="form-editor-coach" />
          <TA label="Benötigtes Material" value={f.material} onChange={(v) => set("material", v)} testId="form-editor-mat" />
          <TA label="Variationen" value={f.variationen} onChange={(v) => set("variationen", v)} testId="form-editor-var" />
          <TA label="Tipps / Korrekturen" value={f.tipps} onChange={(v) => set("tipps", v)} testId="form-editor-tipps" />

          <div>
            <ImageField
              value={(f.fotos || [])[0] || null}
              onChange={(d) => set("fotos", d ? [d] : [])}
              testId="form-editor-image"
              accent="emerald"
            />
          </div>
        </div>

        <div className="shrink-0 border-t border-zinc-800 p-4 flex gap-2 bg-zinc-950"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
          <Button variant="outline" onClick={onClose}
            className="flex-1 bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900 h-11"
            data-testid="form-editor-cancel">
            Abbrechen
          </Button>
          <Button onClick={save} disabled={saving}
            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold h-11 shadow-lg shadow-emerald-950/30" data-testid="form-editor-save">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Speichern…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NumField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[10px] sm:text-[11px] uppercase tracking-widest text-emerald-400/80 font-semibold">{label}</Label>
      <Input type="number" inputMode="numeric" value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
        className="bg-zinc-800/60 border-zinc-700/60 text-zinc-100 mt-1 h-11 focus-visible:ring-2 focus-visible:ring-emerald-500/50 focus-visible:border-emerald-500"
        data-testid={testId} />
    </div>
  );
}

function TA({ label, value, onChange, rows = 2, testId }) {
  return (
    <div>
      <Label className="text-[10px] sm:text-[11px] uppercase tracking-widest text-emerald-400/80 font-semibold">{label}</Label>
      <AutoTextarea value={value} onChange={onChange} minRows={rows}
        className="w-full mt-1 bg-zinc-800/60 border border-zinc-700/60 text-zinc-100 rounded-md px-3 py-2.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500"
        data-testid={testId} />
    </div>
  );
}
